import { Clipboard } from '@angular/cdk/clipboard';
import {
   ChangeDetectionStrategy,
   Component,
   EventEmitter,
   Input,
   OnDestroy,
   Output,
   ViewEncapsulation,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { PhoneTypes } from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EntityTable } from 'src/app/common-components/register/models/register-view';
import { DeviceDetailSaveEvent } from 'src/app/pages/device-details/models/device-detail-form-submit-event';
import { DevicePrivileges } from 'src/app/pages/device-register/models/device-privileges';
import { DeviceRegisterListItem } from 'src/app/pages/device-register/models/device-register-results.model';
import { ExtensionHackedFraudDetails } from 'src/app/pages/extension-details/helpers/hacked-fraud-helper';
import { ExtensionPhoneOrderingOptions, PhoneRegisterOptions } from '../../models/phone-register-options';
import { PhoneRegisterListItem } from '../../models/phone-register-results.model';
import { PhoneTypeOption } from '../../models/phone-type-option';
import * as ExtensionPhoneRegisterActions from '../state/extension-phone-register.actions';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-extension-phone-register-view',
    styleUrls: ['./extension-phone-register-view.component.scss'],
    templateUrl: './extension-phone-register-view.component.html',
    standalone: false
})
export class ExtensionPhoneRegisterViewComponent
   extends EntityTable<PhoneRegisterListItem, PhoneRegisterOptions, ExtensionPhoneOrderingOptions>
   implements OnDestroy
{
   @Input() includeInactive: boolean;

   @Input() canEdit: boolean;

   @Input() canDelete: boolean;

   @Input() canAdd: boolean;

   @Input() accountId: number | null;

   @Input() isSystemUser: boolean;

   @Input() hackedFraudDetails: ExtensionHackedFraudDetails | null;

   @Output() edit = new EventEmitter<PhoneRegisterListItem>();

   @Output() delete = new EventEmitter<number>();

   @Output() createPhoneTypeChange = new EventEmitter<PhoneTypeOption>();

   @Output() resetHackedAndFraud = new EventEmitter<number>();

   @Output() rebootPhone = new EventEmitter<number>();

   @Output() resetPhoneZtp = new EventEmitter<number>();

   @Output() deviceReleased = new EventEmitter<DeviceDetailSaveEvent>();

   @Output() releaseToAccount = new EventEmitter<DeviceRegisterListItem>();

   @Output() deleteDevice = new EventEmitter<{ deviceId: number }>();

   @Input() createPhoneTypeOptions: PhoneTypeOption[] | null;

   selectedCreateExtensionType: PhoneTypeOption | undefined = undefined;

   invalidPhoneIds: number[] = [];

   phoneTypes = PhoneTypes;

   @Input() devicePrivileges: DevicePrivileges;

   private destroyed$ = new Subject<void>();

   constructor(store: Store, private actions$: Actions, private clipboard: Clipboard, private snackbar: MatSnackBar) {
      super(store);

      this.resetHackedAndFraud = new EventEmitter<number>();

      this.actions$
         .pipe(ofType(ExtensionPhoneRegisterActions.updateInvalidPhones), takeUntil(this.destroyed$))
         .subscribe((action) => {
            this.invalidPhoneIds = [...action.invalidPhoneIds];
         });
   }

   resetHackedAndFraudDetails(phone) {
      this.resetHackedAndFraud.emit(phone.sipUsers[0].sipUserId);
   }

   rebootPhoneClick(phone) {
      this.rebootPhone.emit(phone.devices[0].deviceId);
   }

   resetPhoneZtpClick(phone) {
      this.resetPhoneZtp.emit(phone.devices[0].deviceId);
   }

   onRowClick(entity: PhoneRegisterListItem) {
      if (this.canEdit && entity.phoneType !== PhoneTypes.Appsuite) {
         this.onEditClick(entity);
      }
   }

   onEditClick(entity: PhoneRegisterListItem) {
      this.edit.emit(entity);
   }

   onDeleteClick(entity: PhoneRegisterListItem) {
      if (entity.phoneId) {
         this.delete.emit(entity.phoneId);
      }
   }

   copyMacAddressToClipboard(entity: PhoneRegisterListItem) {
      if (entity.name) {
         const copied: boolean = this.clipboard.copy(entity.name.substring(entity.name.indexOf('- ') + 2));

         if (copied) {
            this.snackbar.open('Copied to Clipboard');
         } else {
            this.snackbar.open('Failed to Copy');
         }
      }
   }

   copySipUsernameToClipboard(entity: PhoneRegisterListItem) {
      if (entity && entity.sipUsers && entity.sipUsers[0].userName) {
         const copied: boolean = this.clipboard.copy(entity.sipUsers[0].userName);

         if (copied) {
            this.snackbar.open('Copied to Clipboard');
         } else {
            this.snackbar.open('Failed to Copy');
         }
      }
   }

   copyWebInterfaceUrl(phone: PhoneRegisterListItem) {
      const handset = phone?.baseStationHandsets?.[0];
      const protocol: 'http' | 'https' = handset ? 'http' : 'https';
      const ip = phone?.sipUsers?.[0]?.internalIps ?? phone?.sipUsers?.[0]?.externalIps;

      if (!ip) return;

      let url = `${protocol}://${ip}`;

      const copied = this.clipboard.copy(url);
      if (copied) {
         this.snackbar?.open('URL copied to clipboard.');
      }
   }

   copyCurrentWebInterfacePassword(phone: PhoneRegisterListItem) {
      const password = phone.adminPassword;
      if (!password) return;

      const copied = this.clipboard.copy(password);
      if (copied) {
         this.snackbar?.open('Password copied to clipboard.');
      }
   }

   onCreatePhoneTypeSelected(phoneTypeOption: PhoneTypeOption): void {
      this.createPhoneTypeChange.emit(phoneTypeOption);
   }

   getActionsButtonId(_: number, phoneRegisterListItem: PhoneRegisterListItem): string {
      return `extension-phone-register-${phoneRegisterListItem.phoneId}-row-action-button`;
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   canReleaseToAccount(device: DeviceRegisterListItem) {
      return this.devicePrivileges.canEdit && this.isSystemUser;
   }

   canReleaseToSystem(device: DeviceRegisterListItem) {
      return this.devicePrivileges.canEdit && this.devicePrivileges.canDelete && this.isSystemUser;
   }

   canDeleteFromNDT(device: DeviceRegisterListItem) {
      return this.devicePrivileges.canDelete && this.isSystemUser$;
   }

   onReleaseToSystemClick(device: DeviceRegisterListItem): void {
      const releaseEventModel: DeviceDetailSaveEvent = {
         device: {
            accountId: null,
            deviceId: device?.deviceId ?? null,
         },
         deviceFields: ['accountId'],
      };

      this.deviceReleased.emit(releaseEventModel);
   }

   onReleaseToAccountClick(device: DeviceRegisterListItem): void {
      this.releaseToAccount.emit(device);
   }

   onDeleteFromSystemClick(device: DeviceRegisterListItem): void {
      if (device?.deviceId) {
         this.deleteDevice.emit({
            deviceId: device.deviceId,
         });
      }
   }
}
